/* Email Center — wraps workspace SMTP settings + recent submission log + email templates. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Zap, CheckCircle2, XCircle, Copy } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const TEMPLATES = [
  { id: "submit", name: "Candidate submission", subject: "Candidate submission — {name} · {title}", body: "Hi team,\n\nPlease find attached the profile of {name} ({title}).\n\nHighlights:\n• {top_skills}\n• {experience_summary}\n\nHappy to set up an intro call — let me know your availability this week.\n\nBest,\n{sender}" },
  { id: "interview", name: "Interview invite", subject: "Interview scheduled — {name}", body: "Hi {candidate_first},\n\nGreat news — {client} would like to speak with you. Proposed slots:\n\n• Option 1: [date/time]\n• Option 2: [date/time]\n\nPlease confirm the one that works.\n\nBest,\n{sender}" },
  { id: "followup", name: "Follow-up", subject: "Following up on {name}'s profile", body: "Hi team,\n\nJust following up on the profile of {name} we shared last week. Happy to answer any questions or set up an intro call.\n\nBest,\n{sender}" },
  { id: "reject", name: "Rejection (kind)", subject: "Update on your application — {role}", body: "Hi {candidate_first},\n\nThanks for your time. Unfortunately the team has decided to move forward with other candidates for the {role} role. Your profile stays in our active library — we'll reach out as soon as a fit opens up.\n\nBest,\n{sender}" },
  { id: "docs", name: "Document request", subject: "One more document needed — {name}", body: "Hi {candidate_first},\n\nCould you share the following before Friday so we can move forward:\n\n• Government-issued ID\n• Latest payslip\n• Two references\n\nThanks,\n{sender}" },
];

export default function EmailCenter() {
  const [cfg, setCfg] = useState(null);
  const [recent, setRecent] = useState([]);
  const [selected, setSelected] = useState(TEMPLATES[0]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/email/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/email/logs").then((r) => setRecent((r.data || []).slice(0, 8))).catch(() => {});
  }, []);

  const copy = (t) => { navigator.clipboard.writeText(`Subject: ${t.subject}\n\n${t.body}`); toast.success("Template copied to clipboard"); };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold flex items-center gap-2"><Mail className="size-3.5" /> Email Center</div>
        <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">Outbound workspace</h1>
        <p className="text-muted-foreground mt-1 text-sm">Provider config, ready-to-use email templates, and recent delivery log.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-border lg:col-span-1">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="display font-bold text-lg">Provider</div>
              {cfg?.provider && cfg.provider !== "none" ? (
                <span className="chip bg-emerald-500/10 text-emerald-600 border-emerald-500/30 flex items-center gap-1"><CheckCircle2 className="size-3" /> {cfg.provider}</span>
              ) : (
                <span className="chip bg-rose-500/10 text-rose-600 border-rose-500/30 flex items-center gap-1"><XCircle className="size-3" /> Not set</span>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-1">{cfg?.from_email || "Not configured"}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{cfg?.from_name}</div>
            <Button variant="outline" className="mt-4 w-full gap-2 lift" onClick={() => navigate("/settings")} data-testid="ec-goto-settings">
              <Zap className="size-4" /> Configure in Settings
            </Button>
          </CardContent>
        </Card>

        <Card className="border-border lg:col-span-2">
          <CardContent className="p-5">
            <div className="display font-bold text-lg mb-3">Recent deliveries</div>
            {recent.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-6">No emails sent yet.</div>
            ) : (
              <div className="divide-y divide-border">
                {recent.map((l) => (
                  <div key={l.id} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`ec-recent-${l.id}`}>
                    {l.status === "sent" ? <CheckCircle2 className="size-4 text-emerald-500 shrink-0" /> : <XCircle className="size-4 text-rose-500 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{l.resume?.candidate_name || l.resume?.title}</div>
                      <div className="text-xs text-muted-foreground truncate">→ {l.to_email}</div>
                    </div>
                    <div className="text-[11px] text-muted-foreground">{new Date(l.sent_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-border mt-4">
        <CardContent className="p-5">
          <div className="display font-bold text-lg mb-3">Email templates</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              {TEMPLATES.map((t) => (
                <button key={t.id} onClick={() => setSelected(t)} className={`w-full text-left px-3 py-2 rounded-md text-sm ${selected.id === t.id ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-secondary/80"}`} data-testid={`ec-tpl-${t.id}`}>
                  {t.name}
                </button>
              ))}
            </div>
            <div>
              <Label className="text-xs font-semibold">Subject</Label>
              <Input readOnly value={selected.subject} className="mb-2" data-testid="ec-tpl-subject" />
              <Label className="text-xs font-semibold">Body</Label>
              <Textarea readOnly rows={9} value={selected.body} className="font-mono text-[12px]" data-testid="ec-tpl-body" />
              <Button size="sm" variant="outline" className="mt-2 gap-1.5" onClick={() => copy(selected)} data-testid="ec-tpl-copy"><Copy className="size-3.5" /> Copy template</Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Placeholders like <code>{"{name}"}</code>, <code>{"{title}"}</code>, <code>{"{role}"}</code>, <code>{"{client}"}</code>, <code>{"{sender}"}</code> are hints — currently templates are copy-paste helpers. Full auto-substitution coming next.</p>
        </CardContent>
      </Card>
    </div>
  );
}
